/**
 * User permission helpers for reader vs builder/editor.
 * Used to show "review chart design" and builder navigation only to non-readers.
 */

import type { User } from "../store/userStore";

const PAT_ELIGIBLE_PERMISSIONS = new Set([
  "administrator",
  "power_user",
  "superuser",
]);

const WRITE_CAPABLE_PERMISSIONS = new Set([
  "administrator",
  "publisher",
  "contributor",
  "superuser",
]);

function permissionValuesFromUser(currentUser: User): string[] {
  const fromProjects = collectPermissionValues(currentUser.permissions);
  if (fromProjects.length > 0) {
    return fromProjects;
  }
  const highest = currentUser.permissions_highest;
  if (typeof highest === "string" && highest.length > 0) {
    return [highest];
  }
  return [];
}

/**
 * Collect permission values from user.permissions (string | array | object).
 */
export function collectPermissionValues(
  permissions: User["permissions"] | string | Record<string, string> | undefined
): string[] {
  if (!permissions) {
    return [];
  }
  if (typeof permissions === "string") {
    return [permissions];
  }
  if (Array.isArray(permissions)) {
    return permissions.filter((p): p is string => typeof p === "string");
  }
  if (typeof permissions === "object") {
    return Object.values(permissions).filter(
      (p): p is string => typeof p === "string"
    );
  }
  return [];
}

/**
 * PAT tokens are available only for administrators and power users (and super user).
 */
export function canUsePatTokens(currentUser: User | null): boolean {
  if (!currentUser) {
    return false;
  }
  if (currentUser.is_super_user === true) {
    return true;
  }
  const permissionValues = permissionValuesFromUser(currentUser);
  if (permissionValues.length === 0) {
    return false;
  }
  return permissionValues.some((p) => PAT_ELIGIBLE_PERMISSIONS.has(p));
}

/**
 * True when the user is an administrator (super users always qualify).
 */
export function isAdministrator(currentUser: User | null): boolean {
  if (!currentUser) {
    return false;
  }
  if (currentUser.is_super_user === true) {
    return true;
  }
  return permissionValuesFromUser(currentUser).includes("administrator");
}

/**
 * True for administrators and publishers (super users always qualify).
 *
 * This is the gate that reaching Dataset Info / Day Info already implies: Datasets.tsx only shows
 * the Edit button to those roles. `isAdministrator` alone is STRICTER than that, so gating a
 * control on it would leave a publisher on a page whose buttons they cannot see; `hasWritePermission`
 * is looser (it also admits contributors). Prefer this for actions on those two pages.
 */
export function isAdminOrPublisher(currentUser: User | null): boolean {
  if (!currentUser) {
    return false;
  }
  if (currentUser.is_super_user === true) {
    return true;
  }
  const permissionValues = permissionValuesFromUser(currentUser);
  return permissionValues.includes("administrator") || permissionValues.includes("publisher");
}

/** Admin menus a publisher or contributor may open. Everyone else on the admin page stays hidden. */
export const LIMITED_ADMIN_MENU_IDS = ["streaming", "crew-roster", "crew", "baselines"] as const;

const LIMITED_ADMIN_PERMISSIONS = new Set(["publisher", "contributor"]);

function hasPublisherOrContributor(currentUser: User): boolean {
  return permissionValuesFromUser(currentUser).some((p) => LIMITED_ADMIN_PERMISSIONS.has(p));
}

/**
 * Administrator Settings in the sidebar, and the /admin page.
 * Administrators, super users, publishers, and contributors.
 */
export function canOpenAdminPage(currentUser: User | null): boolean {
  if (!currentUser) {
    return false;
  }
  if (isAdministrator(currentUser)) {
    return true;
  }
  return hasPublisherOrContributor(currentUser);
}

/**
 * Publisher or contributor who is not an administrator or super user.
 * Those accounts see only streaming, crew roster, crew, and baselines, and not server status.
 */
export function hasLimitedAdminMenus(currentUser: User | null): boolean {
  if (!currentUser || isAdministrator(currentUser)) {
    return false;
  }
  return hasPublisherOrContributor(currentUser);
}

export function isLimitedAdminMenu(menuId: string): boolean {
  return (LIMITED_ADMIN_MENU_IDS as readonly string[]).includes(menuId);
}

/**
 * True when the user has write-capable project permission (not power_user / reader).
 */
export function hasWritePermission(currentUser: User | null): boolean {
  if (!currentUser) {
    return false;
  }
  if (currentUser.is_super_user === true) {
    return true;
  }
  const permissionValues = permissionValuesFromUser(currentUser);
  if (permissionValues.length === 0) {
    return false;
  }
  return permissionValues.some((p) => WRITE_CAPABLE_PERMISSIONS.has(p));
}

/**
 * Write permission on one project. A reader (or power user) on that project is excluded
 * even when they can write on a different project.
 */
export function hasProjectWritePermission(currentUser: User | null, projectId: number): boolean {
  if (!currentUser || !Number.isFinite(projectId) || projectId <= 0) {
    return false;
  }
  if (currentUser.is_super_user === true) {
    return true;
  }
  const permissions = currentUser.permissions;
  if (permissions && typeof permissions === "object" && !Array.isArray(permissions)) {
    const value = (permissions as Record<string, unknown>)[String(projectId)];
    return typeof value === "string" && WRITE_CAPABLE_PERMISSIONS.has(value);
  }
  return hasWritePermission(currentUser);
}

/**
 * Returns true if the user has only "reader" permission (cannot edit/build).
 * Superusers are never considered readers for feature gating.
 */
export function isReader(currentUser: User | null): boolean {
  if (!currentUser) return true; // No user = treat as reader (no builder actions)
  if (currentUser.is_super_user === true) return false;

  const userPermissions = currentUser.permissions;
  if (typeof userPermissions === "string") {
    return userPermissions === "reader";
  }
  if (Array.isArray(userPermissions)) {
    return (
      userPermissions.length > 0 &&
      userPermissions.every((p) => p === "reader")
    );
  }
  if (typeof userPermissions === "object" && userPermissions !== null) {
    const values = Object.values(userPermissions);
    return values.length > 0 && values.every((p) => p === "reader");
  }
  return true;
}
